<script setup>
import { Link, router, usePage } from '@inertiajs/vue3'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { ChevronDownIcon, UserCircleIcon } from '@heroicons/vue/24/outline'
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '@stores/AuthStore.js'
import { resetClientStores } from '@stores/resetClientStores.js'

const authStore = useAuthStore()

// Feature flag (subscription vertical): when off, the header shows no sign-in /
// account entry point at all. Sourced server-side from finder's config table and
// shared via Inertia, so it's available at first render (no flash) and fail-closed.
const page = usePage()
const loginEnabled = computed(() => !!page.props.features?.subscription_login)

// Hydration guard: AuthStore is empty during SSR, so render nothing until mounted.
const mounted = ref(false)
onMounted(() => {
  mounted.value = true
})

const isAuthed = computed(() => mounted.value && authStore.isAuthenticated)
// The trigger shows the client's name when we have it, otherwise just the user
// icon — never a generic "My account" label or the raw email. The name comes
// from the in-memory client hydrated on load (app.js); the account/sign-out
// labels live inside the dropdown.
const displayName = computed(() => authStore.client?.name?.trim() || '')

const signOut = () => {
  authStore.clearSession()
  resetClientStores()
  router.visit('/')
}
</script>

<template>
  <Menu v-if="isAuthed && loginEnabled" as="div" class="relative">
    <MenuButton dusk="account-menu-button"
      class="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-white hover:bg-gray-900 hover:bg-opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
      <UserCircleIcon class="h-6 w-6 shrink-0" />
      <span v-if="displayName" class="hidden sm:inline max-w-[10rem] truncate">{{ displayName }}</span>
      <ChevronDownIcon class="hidden sm:inline h-4 w-4 shrink-0" />
    </MenuButton>

    <MenuItems
      class="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
      <MenuItem v-slot="{ active }">
        <Link href="/membership" dusk="account-menu-abn-books"
          :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']">
          ABN Books
        </Link>
      </MenuItem>
      <MenuItem v-slot="{ active }">
        <Link href="/account" dusk="account-menu-link"
          :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']">
          My account
        </Link>
      </MenuItem>
      <MenuItem v-slot="{ active }">
        <button type="button" @click="signOut" dusk="account-sign-out"
          :class="[active ? 'bg-gray-100' : '', 'block w-full px-4 py-2 text-left text-sm text-gray-700']">
          Sign out
        </button>
      </MenuItem>
    </MenuItems>
  </Menu>

  <Link v-else-if="mounted && loginEnabled" href="/login" dusk="header-sign-in"
    class="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-white hover:bg-gray-900 hover:bg-opacity-50">
    <UserCircleIcon class="h-6 w-6 shrink-0" />
    <span class="hidden sm:inline">Sign in</span>
  </Link>
</template>
